/**
 * AB#22077 — the audit log: who did what, to what, and when.
 *
 * Three permissions decide what a person may read, checked by the backend on
 * every call and here only to show or hide the surfaces:
 *   view-audit-log        ACL → Audit log (implies the next)
 *   view-record-activity  the Activity tab on a record, "Last changed by"
 *   export-audit-log      the CSV
 */
import { computed, ref } from 'vue';

import { can } from '@/composables';
import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';

export const AUDIT_VIEW = 'view-audit-log';
export const AUDIT_EXPORT = 'export-audit-log';
export const AUDIT_RECORD = 'view-record-activity';

export const canViewAuditLog = (): boolean => can(AUDIT_VIEW);
export const canViewRecordActivity = (): boolean => can(AUDIT_RECORD) || can(AUDIT_VIEW);
export const canExportAuditLog = (): boolean => can(AUDIT_VIEW) && can(AUDIT_EXPORT);

export type AuditOutcome = 'allowed' | 'refused' | 'failed';
export type AuditSource = 'portal' | 'impersonation' | 'api' | 'system';

export interface AuditActor {
  type: 'user' | 'api_key' | 'system';
  id: number | null;
  name: string | null;
  role: string | null;
}

export interface AuditChange {
  model: string;
  model_id: number | null;
  field: string;
  old: string | null;
  new: string | null;
  old_label: string | null;
  new_label: string | null;
}

export interface AuditEntry {
  id: number;
  at: string;
  actor: AuditActor;
  impersonator: { id: number; name: string | null } | null;
  action: string;
  action_label: string;
  area: string;
  area_label: string;
  subject: { type: string; id: number; label: string | null } | null;
  summary: string;
  outcome: AuditOutcome;
  source: AuditSource;
  changes_count: number;
  // Only on a full entry.
  route?: string | null;
  method?: string | null;
  ip?: string | null;
  user_agent?: string | null;
  request_id?: string | null;
  duration_ms?: number | null;
  context?: Record<string, unknown> | null;
  changes?: AuditChange[];
}

export interface AuditOptions {
  actors: { type: string; id: number; name: string; entries: number }[];
  areas: { value: string; label: string }[];
  actions: { action: string; area: string; label: string }[];
  outcomes: { value: AuditOutcome; label: string }[];
  sources: { value: AuditSource; label: string }[];
  can_export: boolean;
}

export interface AuditFilters {
  q: string;
  actor_id: string;
  area: string;
  action: string;
  outcome: string;
  source: string;
  from: string;
  to: string;
  changes_only: boolean;
  /** Set from an entry's "All activity on this record"; not in the filter band. */
  subject_type: string;
  subject_id: string;
  /** The page reads the recent months unless this is set (or a date / record is). */
  all: boolean;
}

export const emptyFilters = (): AuditFilters => ({
  q: '',
  actor_id: '',
  area: '',
  action: '',
  outcome: '',
  source: '',
  from: '',
  to: '',
  changes_only: false,
  subject_type: '',
  subject_id: '',
  all: false,
});

export const AUDIT_PAGE_SIZES = [25, 50, 100, 200];
export const AUDIT_DEFAULT_PAGE_SIZE = 50;

/** The quick chips above the table: each is a preset of the filters. */
export const QUICK_CHIPS: {
  id: string;
  label: string;
  apply: (f: AuditFilters) => AuditFilters;
}[] = [
  { id: 'today', label: 'Last 24 h', apply: (f) => ({ ...f, from: isoDaysAgo(1), to: '' }) },
  { id: 'refused', label: 'Refused only', apply: (f) => ({ ...f, outcome: 'refused' }) },
  { id: 'impersonation', label: 'Impersonated', apply: (f) => ({ ...f, source: 'impersonation' }) },
  { id: 'finance', label: 'Money changes', apply: (f) => ({ ...f, area: 'finance' }) },
  { id: 'acl', label: 'Permission changes', apply: (f) => ({ ...f, area: 'acl' }) },
];

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const p = (n: number): string => String(n).padStart(2, '0');

  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:00`;
}

/** Which filters are set, as query parameters; empty ones are left out. */
export const filterParams = (f: AuditFilters): Record<string, string> => {
  const out: Record<string, string> = {};
  (Object.keys(f) as (keyof AuditFilters)[]).forEach((k) => {
    const v = f[k];
    if (k === 'changes_only' || k === 'all') {
      if (v) out[k] = '1';
    } else if (typeof v === 'string' && v.trim() !== '') {
      out[k] = v.trim();
    }
  });

  return out;
};

export const activeFilterCount = (f: AuditFilters): number =>
  Object.keys(filterParams(f)).filter((k) => k !== 'q' && k !== 'all').length;

/** "2026-10-07 09:41:12" → "7 Oct 2026, 09:41". */
export const whenLabel = (at: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(at ?? '');
  if (!m) return at ?? '';
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  return `${Number(m[3])} ${months[Number(m[2]) - 1]} ${m[1]}, ${m[4]}:${m[5]}`;
};

export const outcomeLabel = (o: AuditOutcome): string =>
  o === 'allowed' ? 'Allowed' : o === 'refused' ? 'Refused' : 'Failed';

export const sourceLabel = (s: AuditSource): string =>
  s === 'portal'
    ? 'Portal'
    : s === 'impersonation'
      ? 'Impersonating'
      : s === 'api'
        ? 'Integration'
        : 'System';

/** A change's value as the page shows it: the record's words beside an id. */
export const changeValue = (value: string | null, label: string | null): string => {
  if (value === null || value === '') return '—';
  if (label && label !== value) return `${label} (${value})`;

  return value;
};

export function useAuditLog() {
  const items = ref<AuditEntry[]>([]);
  const total = ref(0);
  const page = ref(1);
  const perPage = ref(AUDIT_DEFAULT_PAGE_SIZE);
  const lastPage = ref(1);
  /** Days the server limited the list to, or null when it read everything asked for. */
  const windowDays = ref<number | null>(null);
  const filters = ref<AuditFilters>(emptyFilters());
  const options = ref<AuditOptions | null>(null);
  const loading = ref(false);
  const fetching = ref(false);
  const entry = ref<AuditEntry | null>(null);
  const entryLoading = ref(false);

  /** What the shared table reads back: page, size, search, sort. */
  const currentPaginationInfo = computed(() => ({
    page: page.value,
    per_page: perPage.value,
    search: filters.value.q,
    items_for: 'audit-log',
    sort_by: 'occurred_at',
    sort_order: 'desc' as const,
  }));

  const fetchList = async (over: { page?: number; per_page?: number } = {}): Promise<void> => {
    if (over.page) page.value = over.page;
    if (over.per_page) perPage.value = over.per_page;
    fetching.value = true;
    if (!items.value.length) loading.value = true;
    try {
      const { data } = await http.get('audit-log', {
        params: { ...filterParams(filters.value), page: page.value, per_page: perPage.value },
      });
      const r = data?.result ?? {};
      items.value = (r.items ?? []) as AuditEntry[];
      total.value = Number(r.total ?? 0);
      lastPage.value = Number(r.last_page ?? 1);
      windowDays.value = r.window_days == null ? null : Number(r.window_days);
    } catch (e) {
      handleResponseException(e);
    } finally {
      fetching.value = false;
      loading.value = false;
    }
  };

  const fetchOptions = async (): Promise<void> => {
    try {
      const { data } = await http.get('audit-log/options');
      options.value = (data?.result ?? null) as AuditOptions | null;
    } catch (e) {
      handleResponseException(e);
    }
  };

  const open = async (id: number): Promise<void> => {
    entryLoading.value = true;
    entry.value = items.value.find((i) => i.id === id) ?? null;
    try {
      const { data } = await http.get(`audit-log/${id}`);
      entry.value = (data?.result?.entry ?? entry.value) as AuditEntry | null;
    } catch (e) {
      handleResponseException(e);
    } finally {
      entryLoading.value = false;
    }
  };

  const close = (): void => {
    entry.value = null;
  };

  const applyChip = (id: string): void => {
    const chip = QUICK_CHIPS.find((c) => c.id === id);
    if (chip) filters.value = chip.apply(filters.value);
  };

  const clearFilters = (): void => {
    filters.value = emptyFilters();
  };

  /** The CSV, with the same filters, as a download. */
  const exportUrl = (): string => {
    const params = new URLSearchParams(filterParams(filters.value)).toString();
    const base = String((http.defaults?.baseURL ?? '') as string).replace(/\/$/, '');

    return `${base}/audit-log/export${params ? `?${params}` : ''}`;
  };

  const exportCsv = async (): Promise<void> => {
    try {
      const response = await http.get('audit-log/export', {
        params: filterParams(filters.value),
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      handleResponseException(e);
    }
  };

  return {
    items,
    total,
    page,
    perPage,
    lastPage,
    windowDays,
    filters,
    options,
    loading,
    fetching,
    entry,
    entryLoading,
    currentPaginationInfo,
    fetchList,
    fetchOptions,
    open,
    close,
    applyChip,
    clearFilters,
    exportUrl,
    exportCsv,
  };
}

/** One record's own entries: the Activity tab, and "Last changed by". */
export function useRecordActivity(type: string) {
  const items = ref<AuditEntry[]>([]);
  const total = ref(0);
  const page = ref(1);
  const perPage = ref(25);
  const lastPage = ref(1);
  const loading = ref(false);
  const failed = ref(false);
  const last = ref<{
    id: number;
    at: string;
    by: string | null;
    via: string | null;
    summary: string;
  } | null>(null);

  const load = async (
    id: number | string | null | undefined,
    over: { page?: number } = {}
  ): Promise<void> => {
    if (!id) return;
    if (over.page) page.value = over.page;
    loading.value = true;
    failed.value = false;
    try {
      const { data } = await http.get(`audit-log/record/${type}/${id}`, {
        params: { page: page.value, per_page: perPage.value },
      });
      const r = data?.result ?? {};
      items.value = (r.items ?? []) as AuditEntry[];
      total.value = Number(r.total ?? 0);
      lastPage.value = Number(r.last_page ?? 1);
    } catch {
      failed.value = true;
    } finally {
      loading.value = false;
    }
  };

  const loadLast = async (id: number | string | null | undefined): Promise<void> => {
    if (!id) return;
    try {
      const { data } = await http.get(`audit-log/record/${type}/${id}/last-change`);
      last.value = (data?.result?.last ?? null) as typeof last.value;
    } catch {
      last.value = null;
    }
  };

  return { items, total, page, perPage, lastPage, loading, failed, last, load, loadLast };
}

/** "Last changed by Elin Dahl on 7 Oct 2026, 09:38" */
export const lastChangedLine = (
  last: { at: string; by: string | null; via: string | null } | null
): string => {
  if (!last) return '';
  const who = last.by ? ` by ${last.by}` : '';
  const via = last.via ? ` (impersonated by ${last.via})` : '';

  return `Last changed${who}${via} on ${whenLabel(last.at)}`;
};
